/**
 * Browser-side Supabase client.
 *
 * Reads the `LAYER_PUB_SUPABASE_URL` + `LAYER_PUB_SUPABASE_ANON_KEY`
 * env vars (Vite's `envPrefix: "LAYER_PUB_"` ONLY exposes vars with
 * that narrow prefix, defending against accidental leakage of every
 * other `LAYER_*` secret on the env. Backend services continue to
 * read `LAYER_SUPABASE_*` from process.env directly; both names point
 * at the same Supabase project.
 *
 * Session persisted in localStorage (supabase-js's own default) so a
 * staff session survives tab-close / browser-restart / — critically —
 * iOS Safari discarding a backgrounded tab, which was forcing a
 * re-login on every phone visit while desktop (long-lived tab) never
 * noticed. sessionStorage traded a marginally narrower XSS blast radius
 * for that constant mobile re-auth; localStorage is the standard choice
 * for an authenticated dashboard.
 */

import { createClient } from "@supabase/supabase-js";

const url = (import.meta.env.LAYER_PUB_SUPABASE_URL as string | undefined) ?? "";
const anonKey =
  (import.meta.env.LAYER_PUB_SUPABASE_ANON_KEY as string | undefined) ?? "";

if (!url || !anonKey) {
  // We render the login page even with no config — the user just
  // sees a misconfiguration banner. Don't throw at module load.
  // eslint-disable-next-line no-console
  console.warn(
    "[dashboard] Supabase env vars missing — set LAYER_PUB_SUPABASE_URL + LAYER_PUB_SUPABASE_ANON_KEY.",
  );
}

export const supabase = createClient(url || "http://invalid", anonKey || "stub", {
  auth: {
    persistSession: true,
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});
